import { type CSSProperties, useEffect, useMemo, useState } from "react";

import { CurrencyEnum } from "@/__generated__/graphql";

import { HotkeyDecorator } from "@/components/atoms/HotkeyDecorator";
import { PdfPreview } from "@/components/molecules/PdfPreview";
import { Dialog } from "@/components/organisms/Dialog";
import { Mutations, Queries } from "@/constants";
import { EnumColors } from "@/constants/values";
import { useBackendMutation } from "@/hooks/useBackendMutation";
import { useBackendQuery } from "@/hooks/useBackendQuery";
import { useEnumColor } from "@/hooks/useEnumColor";
import { useContextHotkeys } from "@/hooks/useContextTree";
import { useDevice } from "@/hooks/useDevice";
import { State } from "@/state";
import { Utils } from "@/utils";
import { Spinner } from "@/components/ui/spinner";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/components/ui/utils";
import { Typography } from "@/components/ui/typography";
import { TriageEmailConnectionSearchResultStop } from "./TriageNoDocumentOptions";

import type { TriageOptionListItem } from "./TriageSuggestionsPanel";
import {
  toggleLinesSuggestion,
  type TriageSuggestionStub,
  useTriageDetailContext,
} from "./TriageView.context";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

/** The keys that move down and up what was found. */
const NEXT_KEYS = ["arrowdown", "j"];
const PREVIOUS_KEYS = ["arrowup", "k"];

/**
 * How far from the official rate the one paid may be, as a percentage, and
 * still be said to agree with it: a card's own is a couple of points worse.
 * The search finds a document in another currency within the same
 * (``EmailConnectionSearchResultsService.FOREIGN_RATE_SLACK``).
 */
const FOREIGN_RATE_SLACK = 4;

/**
 * What a search of the viewer's mailboxes found for a payment, open to preview:
 * the documents down its side - the one opened, the keyboard on it on their
 * card where the card shows it - and that one beside them, as its mailbox holds
 * it. Enter imports it: the AI
 * reads it as it reads an email forwarded to the business, and the payment has
 * what it drafts to accept. One in a teammate's private mailbox is not to be
 * previewed; imported, the payment waits for them to confirm it.
 */
export function TriageEmailConnectionSearchResultsDialog({
  stops,
}: {
  /** What was found, each a stop of its card. */
  stops: TriageOptionListItem[];
}) {
  const {
    previewingEmailConnectionSearchResult,
    setPreviewingEmailConnectionSearchResult,
  } = useTriageDetailContext();
  const close = () => setPreviewingEmailConnectionSearchResult(null);
  return (
    <Dialog
      isOpen={previewingEmailConnectionSearchResult != null && stops.length > 0}
      onClose={close}
      isConstrained
      renderKey="triage-email-connection-search-results"
    >
      <TriageEmailConnectionSearchResultsDialogContent
        stops={stops}
        onClose={close}
      />
    </Dialog>
  );
}

function TriageEmailConnectionSearchResultsDialogContent({
  stops,
  onClose,
}: {
  stops: TriageOptionListItem[];
  onClose: () => void;
}) {
  const {
    previewingEmailConnectionSearchResult,
    setPreviewingEmailConnectionSearchResult,
    setActiveOptionKey,
    setSelection,
    setImportingEmailConnectionSearchResult,
  } = useTriageDetailContext();
  // On a phone - or in a window as narrow, the dialog the whole of it - the
  // two sides stack: the payment and what was found over the one being looked
  // at, which takes the rest of the screen.
  const { isNarrow } = useDevice();
  // The one being looked at - one the card leaves behind its stop that opens
  // the rest among them.
  const at = Math.max(
    0,
    stops.findIndex(
      (stop) =>
        stop.searchResult!.identifier === previewingEmailConnectionSearchResult,
    ),
  );
  const result = stops[at].searchResult!;
  const { mutate: importResult, loading: isImporting } = useBackendMutation({
    mutation: Mutations.IMPORT_EMAIL_CONNECTION_SEARCH_RESULT,
    // What else was found goes as one is taken, the payment it comes back as
    // saying so. And the payment read again: one in the books already is
    // suggested for it, to review.
    refetchQueries: [Queries.TRIAGE_DETAIL_QUERY],
  });
  // The currency it is filed in, one result at a time: the one the search
  // found it in until the person says otherwise - or, where it found none,
  // as the document prints it ("As printed") until they name one. A bill
  // printing '$' alone was left to the reader's guess, and booked at face
  // value against a payment in another dollar.
  const [chosen, setChosen] = useState<Record<string, string | null>>({});
  const currency =
    result.identifier in chosen
      ? chosen[result.identifier]
      : result.currency || null;
  // Moved, the card's keyboard goes with it.
  const move = (by: number) => {
    const next = stops[Math.min(Math.max(at + by, 0), stops.length - 1)];
    setPreviewingEmailConnectionSearchResult(next.searchResult!.identifier);
    setActiveOptionKey(next.key);
  };
  const importIt = () => {
    if (isImporting) return;
    // Read in its chat: at once, where it was, the draft it is read into -
    // what else was found goes as it is taken.
    if (!result.isPrivate) {
      setImportingEmailConnectionSearchResult(result);
    }
    void importResult({
      variables: {
        input: {
          identifier: result.identifier,
          ...(currency ? { amountCurrency: currency as CurrencyEnum } : {}),
        },
      },
      callbacks: {
        onSuccess: (data) => {
          onClose();
          if (result.isPrivate) {
            State.getState().setSnackbarMessage(
              `Held for ${result.mailbox} to confirm.`,
            );
          } else {
            const imported = data as {
              isProcessing?: boolean;
              primaryTriageSuggestion?: TriageSuggestionStub | null;
            } | null;
            if (!imported?.isProcessing) {
              // No chat to read it: the business has the file already, and
              // it is suggested for the payment - picked, to review whether it
              // pays the document in place of what pays it now, or with it.
              setImportingEmailConnectionSearchResult(null);
              const suggested = imported?.primaryTriageSuggestion;
              if (suggested) {
                setActiveOptionKey(`lines:${suggested.identifier}`);
                toggleLinesSuggestion(suggested, null, setSelection);
              }
            }
          }
        },
        onError: () => setImportingEmailConnectionSearchResult(null),
      },
    });
  };
  useContextHotkeys({
    id: "triage-email-connection-search-results-next",
    combos: NEXT_KEYS,
    callback: () => move(1),
  });
  useContextHotkeys({
    id: "triage-email-connection-search-results-previous",
    combos: PREVIOUS_KEYS,
    callback: () => move(-1),
  });
  useContextHotkeys({
    id: "triage-email-connection-search-results-import",
    combos: "enter",
    callback: importIt,
  });

  return (
    <div
      data-testid="triage-email-connection-search-results-dialog"
      // The first thing in it to take the keyboard, so Enter is its own and
      // not a button's that the dialog would otherwise put it on.
      tabIndex={0}
      className={cn(
        "[outline:none] grid",
        isNarrow
          ? "grid-cols-[minmax(0,1fr)] grid-rows-[auto_minmax(0,1fr)] flex-1 min-h-0"
          : "grid-cols-[minmax(260px,360px)_minmax(0,1fr)] h-[min(80vh,760px)]",
      )}
    >
      <div
        className={cn(
          "flex flex-col min-h-0",
          isNarrow
            ? "max-h-[40dvh] [border-bottom-width:1px] [border-bottom-style:solid]"
            : "[border-right-width:1px] [border-right-style:solid]",
          "border-border",
        )}
      >
        <div className="pl-4 pr-4 pt-4 pb-2">
          <Typography level="title-md">Found in your mailbox</Typography>
        </div>
        <div
          // In by as much as a stop reaches out.
          className="flex-1 min-h-0 overflow-y-auto flex flex-col gap-0.5 pl-4 pr-2 pb-2"
        >
          {stops.map((stop) => (
            <TriageEmailConnectionSearchResultStop
              key={stop.key}
              stop={stop}
              isActive={stop.key === stops[at].key}
              // Clicked, it is the one looked at.
              onPress={() =>
                setPreviewingEmailConnectionSearchResult(
                  stop.searchResult!.identifier,
                )
              }
            />
          ))}
        </div>
        {/* At the foot of what was found, as a table sets the payment over
            what is set against it: the payment they were found for, and under
            it the one being looked at in the payment's currency, where it is in
            another. */}
        <div className="pl-4 pr-4 pt-3 pb-3 [border-top-width:1px] [border-top-style:solid] border-border">
          <TriageEmailConnectionSearchResultPayment result={result} />
        </div>
      </div>
      <div className="flex flex-col min-h-0">
        <div className="flex-1 min-h-0 overflow-y-auto">
          <TriageEmailConnectionSearchResultPreview result={result} />
        </div>
        <div className="flex items-center justify-end gap-2 p-3 [border-top-width:1px] [border-top-style:solid] border-border">
          <Select
            // "As printed" is an item of its own: Radix's Select takes no empty value.
            value={currency ?? "printed"}
            onValueChange={(value) =>
              setChosen((current) => ({
                ...current,
                [result.identifier]: value === "printed" ? null : value,
              }))
            }
          >
            <SelectTrigger
              size="sm"
              aria-label="The currency it is in"
              data-testid="triage-email-connection-search-result-currency"
              className="min-w-28"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="printed">As printed</SelectItem>
              {Object.values(CurrencyEnum).map((code) => (
                <SelectItem
                  key={code}
                  value={code}
                  data-testid={`triage-email-connection-search-result-currency-${code}`}
                >
                  {code}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={onClose}>
            Close
            <HotkeyDecorator hotkey="escape" color="inherit" />
          </Button>
          <Button
            size="sm"
            data-testid="triage-email-connection-search-result-import"
            onClick={importIt}
            disabled={isImporting}
          >
            {isImporting && <Spinner />}
            {result.isPrivate ? `Ask ${result.mailbox} to confirm` : "Import"}
            <HotkeyDecorator hotkey="enter" color="inherit" />
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * The payment they were found for, as the queue's row has it - its account,
 * what it says, how much and when - and under it, where the one being looked
 * at is in another currency (or its bank's statement says it was charged in
 * one), that one as a table sets a document under its payment: what it prints,
 * times the official rate the day the payment posted, what that comes to, and
 * how far that is from what was paid - the statement's rate beside it.
 */
function TriageEmailConnectionSearchResultPayment({
  result,
}: {
  result: NonNullable<TriageOptionListItem["searchResult"]>;
}) {
  const { triage } = useTriageDetailContext();
  const red = useEnumColor(EnumColors.RED);
  const green = useEnumColor(EnumColors.GREEN);
  const { data } = useBackendQuery(Queries.TRIAGE_FOREIGN_CHARGE_QUERY, {
    variables: { id: triage.id ?? "" },
    skip: !triage.id,
  });
  const node = data?.node && "foreignCharge" in data.node ? data.node : null;
  const charge = node?.foreignCharge ?? null;
  const found = Boolean(result.currency && result.amount != null);
  // What it was paid in: its own currency, its bank account's, or - nothing
  // more particular saying - its business's.
  const own = (
    node?.amountCurrency ||
    (triage.financialAccount && "currency" in triage.financialAccount
      ? triage.financialAccount.currency
      : null) ||
    (triage.entity && "currency" in triage.entity
      ? triage.entity.currency
      : null) ||
    ""
  ).toUpperCase();
  const currency = found
    ? result.currency
    : (charge?.amountOriginalCurrency ?? null);
  const foreign = Number(found ? result.amount : (charge?.amountOriginal ?? 0));
  const paid = Math.abs(Number(triage.amount ?? 0));
  const official = Number(
    (found ? result.exchangeRate : null) ??
      charge?.officialAmountOriginalToAmountFx ??
      0,
  );
  const source =
    (found ? result.exchangeRateSource : null) ?? charge?.officialFxSource;
  const bankRate =
    charge?.amountOriginalToAmountFx &&
    charge.amountOriginalCurrency === currency
      ? Number(charge.amountOriginalToAmountFx)
      : null;
  const atOfficial = foreign * official;
  const apart =
    foreign && official ? (paid / foreign / official - 1) * 100 : null;
  const agrees = apart != null && Math.abs(apart) <= FOREIGN_RATE_SLACK;
  const isExact = official > 0 && Math.abs(paid - atOfficial) < 0.005;
  const day = triage.effectiveDate
    ? Utils.fDate(new Date(triage.effectiveDate))
    : "the day it posted";
  // Two currencies, each written as the match table writes it: CA$, US$.
  const isInTwoCurrencies = Boolean(currency && own && currency !== own);
  const money = (code: string, value: number) =>
    new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: code,
    }).format(Math.abs(value));

  return (
    <>
      <div
        data-testid="triage-email-connection-search-results-payment"
        // Out to the edges of the documents over it, and in by as much as
        // they are.
        className="-ml-2 -mr-2 pl-2 pr-2 pt-1.5 pb-1.5 [border-width:1px] [border-style:solid] border-border rounded-md grid grid-cols-[minmax(0,1fr)_auto] gap-x-2"
      >
        <Typography
          level="body-xs"
          className="truncate text-muted-foreground/80"
        >
          {triage.financialAccount?.name}
        </Typography>
        <Typography
          level="body-xs"
          className="text-muted-foreground/80 [justify-self:end]"
        >
          {triage.effectiveDate
            ? Utils.fDate(new Date(triage.effectiveDate))
            : null}
        </Typography>
        <Typography level="title-sm">
          {triage.description || "This payment"}
        </Typography>
        <Typography
          level="title-sm"
          className="[font-variant-numeric:tabular-nums] [justify-self:end]"
        >
          {isInTwoCurrencies
            ? money(own, paid)
            : Utils.fCurrency(triage.amount ?? 0, false, true)}
        </Typography>
      </div>
      {isInTwoCurrencies && currency ? (
        <div
          data-testid="triage-email-connection-search-result-rates"
          data-agrees={agrees}
          className="-ml-2 -mr-2 pl-2 pr-2 pt-1.5 grid grid-cols-[minmax(0,1fr)_auto] items-baseline gap-x-2 gap-y-0.5"
        >
          <Typography
            level="body-xs"
            className="truncate text-muted-foreground/80"
          >
            {found ? result.filename || result.subject : "Charged"}
          </Typography>
          <Typography
            level="title-sm"
            data-part="printed"
            className="[font-variant-numeric:tabular-nums] [justify-self:end]"
          >
            {money(currency, foreign)}
          </Typography>
          <Typography
            level="body-xs"
            data-part="source"
            className="text-muted-foreground/80"
          >
            {official ? [source, day].filter(Boolean).join(", ") : null}
          </Typography>
          <Typography
            level="body-sm"
            data-part="sum"
            className="[font-variant-numeric:tabular-nums] [justify-self:end]"
          >
            {official ? (
              <>
                <span className="text-muted-foreground/80">
                  {`× ${official.toFixed(4)} = `}
                </span>
                {/* In bold: what is set against the payment. */}
                <span className="font-semibold">{money(own, atOfficial)}</span>
              </>
            ) : (
              `No ${source ?? "official"} rate yet`
            )}
          </Typography>
          <Typography level="body-xs" className="text-muted-foreground/80">
            {bankRate != null ? `Statement says ${bankRate.toFixed(4)}` : null}
          </Typography>
          {apart != null ? (
            // How far what was paid is from it, as the match table says it: on
            // its colour's own ground - a card's rate, a little worse than the
            // official one, agrees.
            <Typography
              level="body-sm"
              component="span"
              data-testid="triage-email-connection-search-result-rates-apart"
              className="[justify-self:end] w-fit pl-1.5 pr-1.5 rounded-sm text-(--apart-color) bg-(--apart-background)"
              style={
                {
                  "--apart-color": agrees ? green.color : red.color,
                  "--apart-background": agrees
                    ? green.backgroundColor
                    : red.backgroundColor,
                } as CSSProperties
              }
            >
              {isExact
                ? "Exact"
                : `${money(own, paid - atOfficial)} apart (${apart >= 0 ? "+" : ""}${apart.toFixed(1)}%)`}
            </Typography>
          ) : null}
        </div>
      ) : null}
    </>
  );
}

/**
 * A document found in a mailbox, fetched from it as it is previewed: a PDF's
 * pages, a picture, the email itself - drawn as a forwarded document's file is,
 * and kept to a frame of its own where it is markup someone else wrote.
 */
function TriageEmailConnectionSearchResultPreview({
  result,
}: {
  result: NonNullable<TriageOptionListItem["searchResult"]>;
}) {
  const { data } = useBackendQuery(
    Queries.EMAIL_CONNECTION_SEARCH_RESULT_PREVIEW_QUERY,
    {
      variables: { id: result.id },
      skip: result.isPrivate,
    },
  );
  const preview =
    data?.node && "preview" in data.node ? data.node.preview : null;
  const url = useMemo(
    () =>
      preview
        ? URL.createObjectURL(
            new Blob(
              [Uint8Array.from(atob(preview.content), (c) => c.charCodeAt(0))],
              { type: preview.mimeType },
            ),
          )
        : null,
    [preview],
  );
  useEffect(() => () => (url ? URL.revokeObjectURL(url) : undefined), [url]);

  if (result.isPrivate) {
    return (
      <div className="p-8 text-center text-muted-foreground">
        <Typography level="title-md">
          In {result.mailbox}&apos;s private mailbox
        </Typography>
        <Typography level="body-sm">
          It is theirs to confirm: asked, {result.mailbox} looks at it, and the
          payment waits for them.
        </Typography>
      </div>
    );
  }
  const mimeType = preview?.mimeType ?? result.mimeType;
  return (
    <Skeleton
      loading={url == null}
      variant="rectangular"
      className="w-full h-full"
    >
      <div
        data-testid="triage-email-connection-search-result-preview"
        className="h-full bg-muted"
      >
        {url == null ? null : mimeType === "application/pdf" ? (
          <PdfPreview key={url} url={url} />
        ) : mimeType.startsWith("image/") ? (
          <img
            src={url}
            alt={result.filename}
            className="w-full h-full object-contain"
          />
        ) : mimeType.startsWith("text/") ? (
          <iframe
            src={url}
            title={result.subject || result.filename}
            // Markup someone else wrote: no script, and no taking the tab.
            sandbox=""
            referrerPolicy="no-referrer"
            className="w-full h-full [border:0px_solid] bg-background"
          />
        ) : (
          <Typography
            level="body-sm"
            className="p-8 text-center text-muted-foreground"
          >
            {result.filename} cannot be shown here. Read, the AI says what it
            is.
          </Typography>
        )}
      </div>
    </Skeleton>
  );
}
